import React, { useState, useRef, useEffect } from 'react';
import { calculateCredits } from '../utils/credits';

const MAX_LENGTH = 1000;

const MODES = [
  {
    key: 'proofread',
    displayName: '교정',
    icon: 'spellcheck',
    title: '교정',
    desc: '맞춤법, 띄어쓰기, 오타 등 문장 오류를 바로잡아요. '
  },
  {
    key: 'copyedit',
    displayName: '교열',
    icon: 'edit_note',
    title: '교열',
    desc: '교정 후, 문법과 문장 구조를 다듬고 표현을 수정해요.'
  },
  {
    key: 'subedit',
    displayName: '윤문',
    icon: 'auto_stories',
    title: '윤문',
    desc: '사용자가 지정한 스타일로 문체를 다듬고 개선해요.   '
  }
];

const MessageInput = ({ onSend, remainingCredits, guestCredits, isAuthenticated }) => {
  const [value, setValue] = useState('');
  const [mode, setMode] = useState(() => localStorage.getItem('otj_mode') || 'proofread');
  const [withReason, setWithReason] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('otj_withReason')) || { proofread: true, copyedit: true };
    } catch {
      return { proofread: true, copyedit: true };
    }
  });
  const [showStyleModal, setShowStyleModal] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    localStorage.setItem('otj_mode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('otj_withReason', JSON.stringify(withReason));
  }, [withReason]);

  // textarea 자동 높이 조절
  useEffect(() => {
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = 'auto';
      ta.style.height = ta.scrollHeight + 'px';
    }
  }, [value]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSubmit = () => {
    if (!value.trim() || value.length > MAX_LENGTH) {
      return;
    }
    
    if (onSend) {
      let options = { text: value, mode };
      if (mode === 'proofread' || mode === 'copyedit') {
        options.includereasons = !!withReason[mode];
      }
      onSend(options);
    }
    setValue('');
  };

  const handleReasonToggle = (key) => {
    setWithReason(prev => {
      const next = { ...prev, [key]: !prev[key] };
      return next;
    });
  };

  // 클립보드 붙여넣기
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) setValue(text);
    } catch (e) {
      // 권한 문제 등 예외 무시
    }
  };

  // 천 단위 콤마 함수
  const formatNumber = n => n.toLocaleString();

  const currentMode = MODES.find(m => m.key === mode);

  return (
    <>
      {/* 카드 전체 접기/펼치기 토글 버튼 - 컴포넌트 최상단 */}
      <div className="w-full flex justify-center mt-0 mb-0">
        <button
          type="button"
          className="bg-white border border-gray-300 rounded-full w-7 h-7 flex items-center justify-center shadow text-gray-500 hover:bg-gray-100 text-base"
          style={{ fontSize: '1.2rem' }}
          onClick={e => {
            e.stopPropagation();
            setCollapsed(prev => !prev);
          }}
          tabIndex={-1}
        >
          {/* 펼쳐진 상태: '∨', 접힌 상태: '∧' */}
          {!collapsed ? '∨' : '∧'}
        </button>
      </div>
      {/* 윤문 스타일 설정 모달 (기본 구조) */}
      {showStyleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-xl shadow-xl p-8 min-w-[320px] max-w-[90vw]">
            <div className="flex justify-between items-center mb-4">
              <div className="text-lg font-bold">윤문 스타일 설정</div>
              <button onClick={() => setShowStyleModal(false)} className="text-gray-400 hover:text-gray-700">
                <span className="material-icons">close</span>
              </button>
            </div>
            <div className="text-gray-500">(여기에 스타일 옵션이 들어갑니다. 추가 지침을 주시면 반영하겠습니다.)</div>
          </div>
        </div>
      )}
      <form className="w-full flex flex-col gap-2 bg-[#f8f9fa] mt-0" onSubmit={e => { e.preventDefault(); handleSubmit(); }}>
        {/* 카드형 옵션 영역 */}
        {!collapsed && (
          <div className="flex gap-1 w-full mb-0 mt-2">
            {MODES.map(opt => {
              let color = '';
              let border = 'border-gray-300';
              let hoverBorder = 'hover:border-blue-400';
              let hoverColor = '';
              if (mode === opt.key) {
                if (opt.key === 'proofread') { color = '#4A90E2'; border = 'border-[#4A90E2]'; hoverBorder = ''; hoverColor = '#4A90E2'; }
                else if (opt.key === 'copyedit') { color = '#7ED37E'; border = 'border-[#7ED37E]'; hoverBorder = ''; hoverColor = '#7ED37E'; }
                else if (opt.key === 'subedit') { color = '#FFC04D'; border = 'border-[#FFC04D]'; hoverBorder = ''; hoverColor = '#FFC04D'; }
              } else {
                if (opt.key === 'proofread') { hoverBorder = 'hover:border-[#4A90E2]'; hoverColor = '#4A90E2'; }
                else if (opt.key === 'copyedit') { hoverBorder = 'hover:border-[#7ED37E]'; hoverColor = '#7ED37E'; }
                else if (opt.key === 'subedit') { hoverBorder = 'hover:border-[#FFC04D]'; hoverColor = '#FFC04D'; }
              }
              // 크레딧 배수 계산
              let creditMultiplier = 1;
              if (opt.key === 'proofread') {
                creditMultiplier = withReason[opt.key] ? 2 : 1;
              } else if (opt.key === 'copyedit') {
                creditMultiplier = withReason[opt.key] ? 4 : 2;
              } else {
                creditMultiplier = 1;
              }
              return (
                <div
                  key={opt.key}
                  className={`flex-1 min-w-[20px] w-full relative flex flex-col items-start gap-1 p-2 rounded-xl border transition-all bg-gray-50 box-border ${border} ${hoverBorder} ${mode === opt.key ? '' : 'opacity-80'} ${mode === opt.key ? '' : 'hover:opacity-100'}`}
                  style={{ cursor: 'pointer', borderColor: border.replace('border-', '') }}
                  onClick={() => setMode(opt.key)}
                >
                  <div className="flex flex-row items-center mb-1 w-full">
                    <span className="material-icons text-2xl card-icon" style={mode === opt.key ? { color } : { color: '#cbd5e1' }}>{opt.icon}</span>
                    <span className="text-lg font-bold card-title ml-2 whitespace-nowrap truncate" style={mode === opt.key ? { color } : { color: '#1e293b' }}>{opt.title}</span>
                    <span className="ml-auto font-extrabold select-none" style={mode === opt.key ? { color, fontSize: '1.2rem' } : { color: '#cbd5e1', fontSize: '1.2rem' }}>
                      ×{creditMultiplier}
                    </span>
                  </div>
                  <span className="text-sm text-left text-gray-500 mb-2">{opt.desc}</span>
                  {opt.key === 'proofread' || opt.key === 'copyedit' ? (
                    <button
                      type="button"
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${withReason[opt.key] ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-blue-50'}`}
                      onClick={e => {
                        e.stopPropagation();
                        handleReasonToggle(opt.key);
                      }}
                    >
                      수정이유포함
                    </button>
                  ) : null}
                  {opt.key === 'subedit' ? (
                    <button
                      type="button"
                      className="px-3 py-1 rounded-full text-xs font-medium border bg-white text-gray-600 border-gray-300 hover:bg-yellow-50 transition-colors"
                      onClick={e => {
                        e.stopPropagation();
                        setShowStyleModal(true);
                      }}
                    >
                      스타일 설정...
                    </button>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
        {/* 크레딧/글자수 카운터 줄 - 카드 아래, sticky 고정 */}
        <div className="flex justify-between items-center px-2 mb-1">
          {isAuthenticated && typeof remainingCredits === 'number' ? (
            <span className="text-sm text-gray-500 font-medium select-none">
              크레딧: <span className="font-bold">{formatNumber(remainingCredits)}</span>
              {value.trim() && (
                <>
                  <span className="mx-1">|</span>
                  <span className="text-orange-600">
                    -{formatNumber(calculateCredits(value, mode, withReason[mode]))}
                  </span>
                </>
              )}
            </span>
          ) : !isAuthenticated ? (
            <span className="text-sm text-gray-500 font-medium select-none">
              게스트 크레딧: <span className="font-bold">{formatNumber(guestCredits)}</span>
              {value.trim() && (
                <>
                  <span className="mx-1">|</span>
                  <span className="text-orange-600">
                    -{formatNumber(calculateCredits(value, mode, withReason[mode]))}
                  </span>
                </>
              )}
            </span>
          ) : (
            <span className="text-sm text-gray-500 font-medium select-none">
              게스트 모드
            </span>
          )}
          <span className="text-sm text-gray-500 font-medium select-none">
            {formatNumber(value.length)}자
          </span>
        </div>
        {/* 입력창 + 버튼 */}
        <div className="flex gap-2 relative">
          <div className="flex flex-col gap-1 w-full">
            <textarea
              ref={textareaRef}
              className={`flex-1 rounded bg-[#f8f9fa] text-gray-900 px-4 py-2 pr-4 focus:outline-none resize-none border border-gray-300`}
              placeholder="최대 1,000자까지 입력할 수 있습니다."
              value={value}
              onChange={e => {
                if (e.target.value.length <= MAX_LENGTH) setValue(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              rows={2}
              style={{ minHeight: 80, overflow: 'hidden' }}
            />
          </div>
          {/* 클립보드 붙여넣기 버튼 오버레이 */}
          {value.length === 0 && (
            <button
              type="button"
              onClick={handlePaste}
              className="absolute top-3 right-32 flex items-center justify-center w-10 h-10 text-gray-400 hover:text-gray-600 bg-gray-100 rounded transition shadow-sm"
              title="클립보드에서 붙여넣기"
              style={{ zIndex: 2 }}
            >
              <span className="material-icons text-2xl">content_copy</span>
            </button>
          )}
          <button
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 rounded transition min-w-[110px] disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!value.trim() || value.length > MAX_LENGTH}
          >
            요청하기
          </button>
        </div>
      </form>
    </>
  );
};

export default MessageInput;